import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BulkAssignHomeDevicesInput,
  BulkGameAccountsResult,
  DeviceDto,
  GameAccountDto,
} from '@gc/shared';

import { api } from '@/lib/api';
import { ghostPillClass, inputClass, pillClass } from './controls';
import {
  HOME_DEVICE_STATUSES,
  assignmentsToApply,
  deviceOptionGroups,
  initialChoices,
  proposeHomeDevices,
  summariseProposals,
  type HomeDeviceChoices,
  type HomeDeviceProposal,
  type HomeDeviceStatus,
} from './gameAccountBulk';
import { isOwnerOnlyRefusal } from './gameAccountForm';
import { toFieldErrors, useResource } from './useSettings';

/**
 * "Assign to PCs by number" (owner, 2026-09-23): a PREVIEW first. Every
 * account's name is read for its number and the PC carrying that number is
 * proposed; rows where two PCs carry it (PC-3 and VPC-3) or none does wait
 * for the owner's pick — until he has made it: reopened, the tool reads a row
 * he already settled as "Already set", so only what is still open is flagged.
 * Nothing is written until Apply, and Apply sends only
 * the rows whose choice differs from what the account already has — the
 * count on the button is the count the server will report back.
 *
 * Reads the whole active list itself rather than the page's: the page may be
 * filtered by a search, and a tool named "assign to PCs" that silently
 * skipped every account the search hid would be a trap.
 */
export function GameAccountsAssignDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (updated: number) => void;
}) {
  const { t } = useTranslation();
  const accounts = useResource<GameAccountDto[]>('/settings/game-accounts');
  const devices = useResource<{ devices: DeviceDto[] }>('/devices?includeHidden=true');

  const deviceList = useMemo(() => devices.data?.devices ?? [], [devices.data]);
  const rows = useMemo(
    () => (accounts.data && devices.data ? proposeHomeDevices(accounts.data, deviceList) : null),
    [accounts.data, devices.data, deviceList],
  );
  const groups = useMemo(() => deviceOptionGroups(deviceList), [deviceList]);
  const deviceNames = useMemo(
    () => new Map(deviceList.map((device) => [device.id, device.name])),
    [deviceList],
  );

  // Only the owner's edits are state; everything else is the proposal.
  const [overrides, setOverrides] = useState<HomeDeviceChoices>({});
  const [show, setShow] = useState<HomeDeviceStatus | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const choices: HomeDeviceChoices = rows ? { ...initialChoices(rows), ...overrides } : {};
  const toApply = rows ? assignmentsToApply(rows, choices) : [];
  const summary = rows ? summariseProposals(rows) : null;
  const shown = rows ? rows.filter((row) => show === 'all' || row.status === show) : [];
  const loadError = accounts.error ?? devices.error;

  function choose(accountId: string, deviceId: string | null): void {
    setOverrides((current) => ({ ...current, [accountId]: deviceId }));
  }

  async function apply(): Promise<void> {
    if (toApply.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: BulkAssignHomeDevicesInput = { assignments: toApply };
      const result = await api.post<BulkGameAccountsResult>(
        '/settings/game-accounts/bulk/home-devices',
        body,
      );
      onDone(result.updated);
    } catch (caught) {
      setError(
        isOwnerOnlyRefusal(caught)
          ? t('settings.gameAccounts.ownerOnly')
          : (toFieldErrors(caught, t('gameAccountTools.assign.failed'))[0]?.message ??
              t('gameAccountTools.assign.failed')),
      );
    } finally {
      setSubmitting(false);
    }
  }

  /** Why a row proposes what it does, in a sentence. */
  function reasonFor(row: HomeDeviceProposal): string | null {
    if (row.status === 'ambiguous') {
      return t('gameAccountTools.assign.ambiguousHint', {
        number: row.number,
        names: row.candidates.map((device) => device.name).join(' / '),
      });
    }
    if (row.status === 'noMatch') {
      return row.number === null
        ? t('gameAccountTools.assign.noNumber')
        : t('gameAccountTools.assign.noPc', { number: row.number });
    }
    if (row.status === 'match' && row.currentDeviceId) {
      return t('gameAccountTools.assign.replaces', {
        name: row.currentDeviceName ?? deviceNames.get(row.currentDeviceId) ?? '—',
      });
    }
    return null;
  }

  /** The PC the account is made for today, or a dash. */
  function renderNow(row: HomeDeviceProposal): ReactNode {
    return row.currentDeviceId ? (
      (row.currentDeviceName ?? deviceNames.get(row.currentDeviceId))
    ) : (
      <span className="text-content-muted">—</span>
    );
  }

  /**
   * The PC select and, on a row two PCs carry, a pick for each. On the phone
   * card the select takes the full width and the picks are thumb-sized.
   */
  function renderChoice(row: HomeDeviceProposal, layout: 'table' | 'card'): ReactNode {
    const choice = choices[row.accountId] ?? null;
    const changes = choice !== row.currentDeviceId;
    return (
      <>
        <select
          value={choice ?? ''}
          onChange={(event) => choose(row.accountId, event.target.value || null)}
          aria-label={t('gameAccountTools.assign.choiceFor', {
            name: row.accountName,
          })}
          className={`${inputClass} ${layout === 'table' ? 'min-w-[9rem] py-1.5' : ''} ${
            changes ? 'border-primary' : ''
          }`}
        >
          <option value="">{t('gameAccountTools.venueWide')}</option>
          {row.currentDeviceId && !deviceNames.has(row.currentDeviceId) ? (
            <option value={row.currentDeviceId}>
              {row.currentDeviceName ?? row.currentDeviceId}
            </option>
          ) : null}
          {groups.map((group) => (
            <optgroup
              key={group.family ?? '*'}
              label={group.family ?? t('gameAccountTools.otherDevices')}
            >
              {group.devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {/* A settled row keeps its choices, the pick lit, so the owner can
            still switch PC-3 for VPC-3. */}
        {row.candidates.length > 1 ? (
          <div className={`flex flex-wrap ${layout === 'card' ? 'mt-2 gap-2' : 'mt-1.5 gap-1.5'}`}>
            {row.candidates.map((device) => (
              <button
                key={device.id}
                type="button"
                aria-pressed={choice === device.id}
                onClick={() => choose(row.accountId, device.id)}
                className={`rounded-full border ${
                  layout === 'card' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-0.5 text-xs'
                } ${
                  choice === device.id
                    ? 'border-primary bg-primary text-primary-fg'
                    : 'border-subtle text-content-secondary hover:bg-hover'
                }`}
              >
                {device.name}
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  function renderBadge(row: HomeDeviceProposal): ReactNode {
    return (
      <span
        className={`inline-block shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}
      >
        {t(`gameAccountTools.assign.statuses.${row.status}`)}
      </span>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-assign-pcs-title"
        className="flex max-h-full w-full max-w-4xl flex-col rounded-card border border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 id="gc-assign-pcs-title" className="text-lg font-semibold text-content">
            {t('gameAccountTools.assign.title')}
          </h2>
          <p className="mt-1 text-sm text-content-muted">{t('gameAccountTools.assign.intro')}</p>

          {summary ? (
            <div
              role="group"
              aria-label={t('gameAccountTools.assign.status')}
              className="mt-3 flex flex-wrap gap-2"
            >
              {(['all', ...HOME_DEVICE_STATUSES] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  aria-pressed={show === status}
                  onClick={() => setShow(status)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    show === status
                      ? 'border-primary bg-primary text-primary-fg'
                      : 'border-subtle text-content-secondary hover:bg-hover'
                  }`}
                >
                  {t(`gameAccountTools.assign.show.${status}`)}{' '}
                  <span className="tabular">
                    {status === 'all' ? (rows?.length ?? 0) : summary[status]}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          {loadError ? (
            <p role="alert" className="p-5 text-sm text-danger">
              {loadError}
            </p>
          ) : rows === null ? (
            <p className="p-5 text-sm text-content-muted">{t('common.loading')}</p>
          ) : shown.length === 0 ? (
            <p className="p-5 text-sm text-content-muted">{t('common.noResults')}</p>
          ) : (
            <>
              {/* Phone: a card per account, everything stacked. The table
                  below is 40rem wide, and on a 375px screen its status and
                  quick picks sat past the edge — the sideways swiping the
                  owner asked to be rid of (iPhone 15, see FilterDisclosure). */}
              <ul className="divide-y divide-subtle md:hidden">
                {shown.map((row) => {
                  const reason = reasonFor(row);
                  return (
                    <li key={row.accountId} data-status={row.status} className="space-y-2 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 break-words text-sm font-semibold text-content">
                          {row.accountName}
                        </p>
                        {renderBadge(row)}
                      </div>
                      <p className="text-xs text-content-secondary">
                        {t('gameAccountTools.assign.now')}: {renderNow(row)}
                      </p>
                      <div>{renderChoice(row, 'card')}</div>
                      {reason ? <p className="text-xs text-content-muted">{reason}</p> : null}
                    </li>
                  );
                })}
              </ul>

              <table className="w-full min-w-[40rem] border-collapse text-sm max-md:hidden">
                <thead className="sticky top-0 bg-raised">
                  <tr className="text-content-secondary">
                    <th className="border-b border-subtle p-3 text-start font-medium">
                      {t('gameAccountTools.assign.account')}
                    </th>
                    <th className="border-b border-subtle p-3 text-start font-medium">
                      {t('gameAccountTools.assign.now')}
                    </th>
                    <th className="border-b border-subtle p-3 text-start font-medium">
                      {t('gameAccountTools.assign.choice')}
                    </th>
                    <th className="border-b border-subtle p-3 text-start font-medium">
                      {t('gameAccountTools.assign.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => {
                    const reason = reasonFor(row);
                    return (
                      <tr key={row.accountId} data-status={row.status}>
                        <td className="border-b border-subtle p-3 text-content">
                          {row.accountName}
                        </td>
                        <td className="border-b border-subtle p-3 text-content-secondary">
                          {renderNow(row)}
                        </td>
                        <td className="border-b border-subtle p-3">{renderChoice(row, 'table')}</td>
                        <td className="border-b border-subtle p-3">
                          {renderBadge(row)}
                          {reason ? (
                            <span className="mt-1 block text-xs text-content-muted">{reason}</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        <footer className="border-t border-subtle p-5">
          {error ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void apply()}
              disabled={submitting || toApply.length === 0}
              className={pillClass}
            >
              {toApply.length === 0
                ? t('gameAccountTools.assign.nothingToApply')
                : t('gameAccountTools.assign.apply', { count: toApply.length })}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/** Status badge colours: a proposal is good news, a question is a warning. */
const STATUS_TONE: Record<HomeDeviceStatus, string> = {
  match: 'bg-primary/15 text-primary',
  unchanged: 'bg-hover text-content-secondary',
  ambiguous: 'bg-ending/20 text-ending',
  noMatch: 'bg-hover text-content-muted',
};
